declare global {
  interface Window {
    nostojs: (callback: (api: any) => void) => void;
    Nosto: any;
  }
  interface NostoAPIResponse {
    recommendations: {
      [placement: string]: Recommendations[];
    };
  }
}

import { writable, get } from "svelte/store";
import type { CheckoutLineItem } from "./basket";
import cfg from "../config/config";
import { account } from "./account";
import { basket } from "./basket";
import { cart } from "./cart";

const isBrowser = typeof window !== "undefined";
const cookiesEnabled = isBrowser ? navigator.cookieEnabled : true;

export type PageType = "PDP" | "PLP" | "HOME" | "SEARCH" | "CART" | "LANDING";

interface Item {
  product_id: string;
  sku_id: string;
  name: string;
  unit_price: number;
  price_currency_code: string;
  quantity: number;
}

interface Cart {
  items: Item[];
}

interface RecItem {
  name: string;
  displaySubtitle: string;
  variantId: string;
  imageUrl: string;
  price: number;
  url: string;
  currencyCode: string;
}

interface SourceRecItem {
  name: string;
  variant_id: string;
  product_id: string;
  image_url: string;
  price: number;
  list_price: number;
  custom_fields: Record<any, any>;
  price_currency_code: string;
  skus: Record<any, any>[];
  tags1: string[];
}

interface RecItem {
  name: string;
  displaySubtitle: string;
  variantId: string;
  productId: string;
  imageUrl: string;
  price: number;
  compareAtPrice: number;
  url: string;
}

interface Recommendations {
  divId: string;
  resultId: string;
  resultTitle: string;
  products: RecItem[];
  pageType: PageType;
  sessionParams: Record<any, any>;
}

function transformBasket(basket: CheckoutLineItem[]): Cart {
  const transformedBasket = basket.map((item: CheckoutLineItem) => {
    return {
      product_id: item.variant?.product?.id?.split("/").splice(-1)[0] || "",
      sku_id: item.variant?.sku || "",
      name: item.title || "",
      unit_price: Number(item.variant?.price.amount || 0),
      price_currency_code: item.variant?.price.currencyCode.toString() || "",
      quantity: Number(item.quantity || 0),
    };
  });

  return { items: transformedBasket };
}

function transformCart(cart): Cart {
  const transformedCart = cart.map(item => {
    return {
      product_id:
        item.node.merchandise?.product?.id?.split("/").splice(-1)[0] || "",
      sku_id: item.node.merchandise?.sku || "",
      name:
        item.node.merchandise?.product?.title +
          " " +
          item.node.merchandise?.title || "",
      unit_price: Number(item.node.merchandise?.price.amount || 0),
      price_currency_code:
        item.node.merchandise?.price.currencyCode.toString() || "",
      quantity: Number(item.node.quantity || 0),
    };
  });

  return { items: transformedCart };
}

function transformRecResults(recs: SourceRecItem[], lang: string): RecItem[] {
  return recs
    .map(rec => {
      return {
        name:
          rec.custom_fields[`global-${lang.toUpperCase()}_Title`] ||
          rec.name ||
          "",
        displaySubtitle:
          rec.custom_fields[`global-${lang.toUpperCase()}_Subtitle`] ||
          rec.custom_fields["global-Display_Subtitle"],
        variantId: rec.variant_id || "",
        productId: rec.product_id || "",
        imageUrl: rec.image_url || "",
        price: rec.price || 0,
        compareAtPrice: rec.list_price || 0,
        url: rec.custom_fields["global-url"],
        sizeGuide: rec.custom_fields["global-Sizeguide"],
        currencyCode: rec.price_currency_code,
        options: [
          {
            __typename: "ProductOptions",
            name: "size",
            values:
              rec?.skus?.map(sku => {
                return sku.custom_fields.size;
              }) || [],
          },
          {
            __typename: "ProductOptions",
            name: "colour",
            values: Array.from(
              new Set(
                rec?.skus?.map(sku => {
                  return sku.custom_fields.colour;
                }) || [],
              ),
            ),
          },
        ],
        tags: rec.tags1,
        variants: {
          edges:
            rec?.skus?.map(sku => {
              return {
                __typename: "VariantEdge",
                node: {
                  __typename: "ProductVariant",
                  id: `gid://shopify/ProductVariant/${sku?.id}`,
                  quantityAvailable: sku?.available === true ? 2 : 0,
                  selectedOptions: [
                    {
                      __typename: "ProductOption",
                      name: "size",
                      value: sku?.custom_fields?.size || undefined,
                    },
                    {
                      __typename: "ProductOption",
                      name: "colour",
                      value: sku?.custom_fields?.colour || undefined,
                    },
                  ],
                  price: {
                    __typename: "Price",
                    amount: sku?.price?.toString() || "",
                    currencyCode: rec.price_currency_code || "",
                  },
                  compareAtPrice: {
                    __typename: "Price",
                    amount: sku?.list_price?.toString() || "",
                    currencyCode: rec.price_currency_code || "",
                  },
                },
              };
            }) || [],
        },
      };
    })
    .filter(rec => rec.url !== "");
}

function transformRecPlacements(page: Record<string, any>, lang: string) {
  const recs = {
    divId: page.div_id,
    resultId: page.result_id,
    resultTitle: page.title,
    products: transformRecResults(page.products, lang),
  };

  return recs;
}

export function recommendationsStore(
  namespace: string,
  localStorage?: Storage,
) {
  let initialState: any = undefined;
  if (localStorage) {
    try {
      const recommendationString = localStorage.getItem(
        `${namespace}:recommendations`,
      );
      initialState = recommendationString
        ? JSON.parse(recommendationString)
        : [];
    } catch {
      initialState = [];
    }
  }
  const recommendations = writable<Recommendations>(initialState);

  // initialise subscription to update local storage
  if (localStorage) {
    recommendations.subscribe(items => {
      localStorage.setItem(
        `${namespace}:recommendations`,
        JSON.stringify(items),
      );
    });
  }

  function getSessionParams() {
    window.nostojs &&
      window.nostojs(api =>
        api.getSearchSessionParams().then(function (response) {
          recommendations.update(recs => {
            return { ...recs, sessionParams: response };
          });
        }),
      );
  }

  function handleNoTracking() {
    if (!cookiesEnabled) {
      window.nostojs && window.nostojs(api => api.visit.setDoNotTrack(true));
    } else {
      window.nostojs && window.nostojs(api => api.visit.setDoNotTrack(false));
    }
  }

  function updatedRecommendations(recs: Recommendations) {
    recommendations.update(() => {
      return recs;
    });
  }

  function setAttribution(resultId: string, productId: string) {
    window.nostojs &&
      window.nostojs(api => {
        api
          .defaultSession()
          .viewProduct(productId)
          .setRef(productId, resultId)
          .update();
      });
  }

  function setUser() {
    const loggedInUser = get(account);
    if (loggedInUser) {
      const { email, userId } = loggedInUser;

      window.nostojs &&
        window.nostojs(api =>
          api.defaultSession().setCustomer({
            customer_reference: userId,
            email,
          }),
        );
    }
  }

  function recordSearch(
    query: string,
    sort: { field: string; order: string }[],
    filter: {
      field: string;
      value?: string | unknown;
      range?: { gte: any; lt: any }[];
    }[],
    hits: { productId: string }[],
    fuzzy: boolean,
    total: number,
    size: number,
    from: number,
  ) {
    if (typeof window == "undefined" || !window.nostojs) return;
    window.nostojs(api =>
      api.recordSearch(
        "serp",
        {
          query,
          products: {
            sort,
            filter,
          },
        },
        {
          products: {
            hits,
            fuzzy,
            total,
            size,
            from,
          },
        },
      ),
    );
  }

  function recordSearchClick(productId: string, url: string) {
    if (typeof window == "undefined" || !window.nostojs) return;
    window.nostojs(api =>
      api.recordSearchClick("serp", { productId: productId, url: url }),
    );
  }

  function recordCategory(
    categoryId: string,
    sort: { field: string; order: string }[],
    filter: {
      field: string;
      value?: string | unknown;
      range?: { gte: any; lt: any }[];
    }[],
    hits: { productId: string }[],
    fuzzy: boolean,
    total: number,
    size: number,
    from: number,
  ) {
    if (typeof window == "undefined" || !window.nostojs) return;
    window.nostojs(api =>
      api.recordSearch(
        "category",
        {
          products: {
            categoryId,
            sort,
            filter,
          },
        },
        {
          products: {
            hits,
            fuzzy,
            total,
            size,
            from,
          },
        },
      ),
    );
  }

  function recordCategoryClick(productId: string, url: string) {
    if (typeof window == "undefined" || !window.nostojs) return;
    window.nostojs(api =>
      api.recordSearchClick("category", { productId: productId, url: url }),
    );
  }

  function setBasket() {
    const currentBasket = get(basket);

    if (currentBasket) {
      window.nostojs &&
        window.nostojs(api => {
          api
            .defaultSession()
            .setCart(transformBasket(currentBasket))
            .viewCart()
            .update();
        });
    }
  }

  function setCart() {
    const currentCart = get(cart);

    const lines = currentCart?.lines?.edges;

    if (lines) {
      window.nostojs &&
        window.nostojs(api => {
          api
            .defaultSession()
            .setCart(transformCart(lines))
            .viewCart()
            .update();
        });
    }
  }

  return {
    subscribe: recommendations.subscribe,
    set: recommendations.set,
    setUser,
    setBasket,
    setCart,
    setAttribution,
    handleNoTracking,
    updatedRecommendations,
    transformRecPlacements,
    getSessionParams,
    recordSearch,
    recordSearchClick,
    recordCategory,
    recordCategoryClick,
  };
}
export const recommendations = recommendationsStore(
  cfg.SHOPIFY_STORE + "-v3",
  isBrowser && navigator.cookieEnabled ? localStorage : undefined,
);
